import {
  getActivityMixFromDesign,
  getProgramActivityAreaFromActivityMix,
} from '@shared/helpers/design/activity-mix.helpers';
import {
  getActivityByType,
  getActivityPropertiesWithDefaults,
} from '@shared/helpers/design/activities.helpers';
import { getDesignGfa } from '@shared/helpers/design/shape.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  ResidentialProgramType,
  resolveResidentialProgramType,
  SchoolProgramType,
  resolveSchoolProgramType,
} from '@shared/models/design/activities/properties';

export type BoverketBtaFields = {
  smahusBTA: number;
  flerbostadshusBTA: number;
  specialbostadBTA: number;
  forskolaBTA: number;
  grundskolaBTA: number;
  gymnasieskolaBTA: number;
  kontorBTA: number;
  handelBTA: number;
  ovrigtBTA: number;
};

const toIntegerBta = (
  niaPart: number,
  niaTotal: number,
  gfa: number,
): number => {
  if (niaTotal <= 0 || gfa <= 0 || niaPart <= 0) return 0;
  return Math.round((niaPart / niaTotal) * gfa);
};

type ResidentialBoverketBtaField =
  'smahusBTA' | 'flerbostadshusBTA' | 'specialbostadBTA';

/**
 * Map residential program type to the Boverket BTA XML field.
 */
const getResidentialBoverketBtaField = (
  programType: ResidentialProgramType,
): ResidentialBoverketBtaField => {
  switch (programType) {
    case ResidentialProgramType.SingleFamily:
      return 'smahusBTA';
    case ResidentialProgramType.Multifamily:
      return 'flerbostadshusBTA';
    case ResidentialProgramType.SpecialPurpose:
      return 'specialbostadBTA';
    default:
      return assertUnreachable(programType);
  }
};

type SchoolBoverketBtaField =
  'forskolaBTA' | 'grundskolaBTA' | 'gymnasieskolaBTA';

/**
 * Map school program type to the Boverket BTA XML field.
 */
const getSchoolBoverketBtaField = (
  programType: SchoolProgramType,
): SchoolBoverketBtaField => {
  switch (programType) {
    case SchoolProgramType.Preschool:
      return 'forskolaBTA';
    case SchoolProgramType.PrimarySchool:
      return 'grundskolaBTA';
    case SchoolProgramType.HighSchool:
      return 'gymnasieskolaBTA';
    default:
      return assertUnreachable(programType);
  }
};

/**
 * Allocate program NIA into Boverket BTA fields: `(NIA_part / NIA_total) * GFA`.
 */
export const calculateBoverketBtaFromDesign = (
  design: BuildingDesign,
): BoverketBtaFields => {
  const empty: BoverketBtaFields = {
    smahusBTA: 0,
    flerbostadshusBTA: 0,
    specialbostadBTA: 0,
    forskolaBTA: 0,
    grundskolaBTA: 0,
    gymnasieskolaBTA: 0,
    kontorBTA: 0,
    handelBTA: 0,
    ovrigtBTA: 0,
  };

  const gfa = getDesignGfa(design);
  const activityMix = getActivityMixFromDesign(design);
  const niaTotal = getProgramActivityAreaFromActivityMix(activityMix, true);
  if (niaTotal <= 0 || gfa <= 0) return empty;

  const scale = (niaPart: number) => toIntegerBta(niaPart, niaTotal, gfa);

  const residentialNia = activityMix[ActivityType.Residential] ?? 0;
  if (residentialNia > 0) {
    const residential = getActivityByType(design, ActivityType.Residential);
    const programType = resolveResidentialProgramType(
      getActivityPropertiesWithDefaults(
        ActivityType.Residential,
        residential?.properties,
      ).program_type,
    );
    empty[getResidentialBoverketBtaField(programType)] = scale(residentialNia);
  }

  const schoolNia = activityMix[ActivityType.School] ?? 0;
  if (schoolNia > 0) {
    const school = getActivityByType(design, ActivityType.School);
    const programType = resolveSchoolProgramType(
      getActivityPropertiesWithDefaults(ActivityType.School, school?.properties)
        .program_type,
    );
    empty[getSchoolBoverketBtaField(programType)] = scale(schoolNia);
  }

  empty.kontorBTA = scale(activityMix[ActivityType.Office] ?? 0);
  empty.handelBTA = scale(
    (activityMix[ActivityType.Retail] ?? 0) +
      (activityMix[ActivityType.Industry] ?? 0),
  );
  // Garage and Unset have no dedicated Boverket typology slot — allocate to ovrigtBTA.
  empty.ovrigtBTA = scale(
    (activityMix[ActivityType.Unset] ?? 0) +
      (activityMix[ActivityType.Garage] ?? 0),
  );

  return empty;
};
